import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { PreferencesUpdateInput } from '@/lib/types';

/**
 * The layout manager's engine: one page's movable SECTIONS — their ORDER, their
 * WIDTH (full / half of the `lg` two-column grid) and which are HIDDEN — as a
 * per-person preference stored AGAINST THE ACCOUNT.
 *
 * Owner, 2026-10-20: "do the full layout manager … in entire emerge", modelled on
 * the LAE app's `layout-manager/usePageLayout.js`. Per person only — no team
 * default, no backend change. Reset means back to the layout declared in code.
 * The components that draw it are `components/layout/` (`LayoutGrid`,
 * `LayoutToolbar`); this file decides nothing about pixels.
 *
 * It follows `use-company-page-layout.ts` wherever the two questions are the
 * same, and says so where they are not.
 *
 * ## Where it lives
 *
 * **`preferences.layouts[pageId] = { v: 1, blocks: [{ id, width, hidden }] }`**,
 * a SIBLING branch of `columns`, `tables` and `companyPage`.
 *
 * ⛔ **NEVER inside `preferences.tables`.** `preferences-store` treats every key
 * there as a saved filter and deletes unknown ones on the next whole-set save.
 *
 * ⛔ **No `localStorage`, not even as a cache.** One authority per preference.
 *
 * ## Why there is NO baseline map and NO reset-on-identity-change
 *
 * The API merges an object over an object RECURSIVELY and replaces arrays
 * wholesale. Every write here names exactly `layouts.<pageId>.v` (a scalar) and
 * `layouts.<pageId>.blocks` (an ARRAY) — so after any write the page's branch is
 * exactly what was sent, with nothing left behind, and every OTHER page's branch
 * is untouched. Reset is `layouts.<pageId>: null`, which deletes the branch.
 * ⚠️ That holds only while those are the only two keys and `blocks` stays an
 * array; add a third key without sending it on every write and this file needs
 * the baseline-and-reset dance `preferences-store` carries.
 *
 * There is deliberately no module-level state: one GET per mount, nothing cached,
 * nothing to forget at sign-out or a support-session switch. The stated gap (as in
 * the company page): a page that stays MOUNTED across an identity switch keeps the
 * previous identity's layout until it remounts.
 *
 * ## The rules
 *
 * 🔴 **The declared default list is the authority, not the stored order.** Saved
 * ids the page no longer declares are DROPPED; ids the page has GAINED are spliced
 * in at their DEFAULT INDEX (the LAE rule, which the contract names — not the
 * company page's nearest-predecessor rule). Labels always come from code, never
 * from storage.
 *
 * 🔴 **A stored preference may only subtract.** Only `hidden: true` hides; a new
 * section arrives visible. An invalid stored width falls back to the default.
 *
 * 🔴 **Three read states, never collapsed:** branch ABSENT → defaults; present
 * and well-formed (`v === 1`, `blocks` an array) → applied; present and MALFORMED
 * → defaults, quietly (and Reset still deletes it). Nothing here throws into a
 * render.
 *
 * A layout that an edit brings back to exactly the defaults is written as a
 * DELETE rather than stored, so a later change to the defaults reaches that
 * person too.
 *
 * ## Writes
 *
 * Edits apply to the screen instantly. Saves are debounced 600 ms, sent ONE AT A
 * TIME and coalesced (each carries the whole branch, so the last to arrive wins),
 * `keepalive` so an edit followed by navigation or F5 still lands; a pending
 * debounce is flushed at unmount and at a `pageId` change. The screen then renders
 * from the RESPONSE unless a newer edit has overtaken it.
 *
 * ⚠️ **Unlike the company page, edits made before the GET returns are HELD**, not
 * refused: they show at once against the defaults and are replayed onto the
 * stored layout when it arrives (then saved). That is safe here because they are
 * kept as operations, not as a whole layout.
 *
 * ⚠️ **A FAILED READ NEVER SAVES.** The defaults (plus any held edits) are shown
 * and `ready` turns true, but nothing is written — writing over a layout we could
 * not read would destroy it. An edit in that state sets `saveError`. (The company
 * page saves after a failed read; the contract for this engine rules otherwise.)
 *
 * A failed write keeps the on-screen layout and exposes `saveError`; the next
 * successful write clears it. The layout is chrome; it never blocks the page.
 *
 * `defaults` may be an inline array: it is keyed on its CONTENT.
 */

export type LayoutWidth = 'full' | 'half';

export interface LayoutBlockDefault {
  id: string;
  label: string;
  /** Defaults to `'full'`. */
  width?: LayoutWidth;
}

export interface LayoutBlock {
  id: string;
  label: string;
  width: LayoutWidth;
  hidden: boolean;
}

export interface PageLayout {
  pageId: string;
  /** The saved layout has loaded (or the read failed → defaults, still `true`). */
  ready: boolean;
  editing: boolean;
  setEditing(next: boolean): void;
  /** Merged order: saved order, unknown ids dropped, new default ids at their default index. */
  blocks: readonly LayoutBlock[];
  /** `false` when hidden or unknown. */
  isVisible(id: string): boolean;
  widthOf(id: string): LayoutWidth;
  /** Move to an index in `blocks` (hidden blocks included). */
  move(id: string, toIndex: number): void;
  /** Step past the next VISIBLE neighbour; a hidden block steps one slot in `blocks`. */
  moveBy(id: string, delta: -1 | 1): void;
  setWidth(id: string, width: LayoutWidth): void;
  toggleHidden(id: string): void;
  /** Deletes the saved layout → defaults. */
  reset(): void;
  /** The current layout equals the defaults. */
  isDefault: boolean;
  /** The last save failure (or a read failure that stops saving), else `null`. */
  saveError: string | null;
}

/* ------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* ------------------------------------------------------------------------- */

const LAYOUTS_KEY = 'layouts';
const LAYOUT_VERSION = 1;
const SAVE_DEBOUNCE_MS = 600;

const SAVE_FAILED = 'The layout could not be saved. It still applies here for now.';
const LOAD_FAILED = 'The saved layout could not be read, so changes here are not saved.';

/** A default with its width resolved. */
export interface ResolvedLayoutDefault {
  id: string;
  label: string;
  width: LayoutWidth;
}

/** One stored entry as read back. `width: null` = absent or invalid → the default applies. */
export interface SavedLayoutEntry {
  id: string;
  width: LayoutWidth | null;
  hidden: boolean;
}

/** One stored entry as written: always explicit. */
export interface StoredLayoutBlock {
  id: string;
  width: LayoutWidth;
  hidden: boolean;
}

export function isLayoutWidth(value: unknown): value is LayoutWidth {
  return value === 'full' || value === 'half';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** The declared defaults, deduplicated (first wins), widths resolved, blank ids dropped. */
export function resolveLayoutDefaults(
  defaults: readonly LayoutBlockDefault[],
): ResolvedLayoutDefault[] {
  const seen = new Set<string>();
  const out: ResolvedLayoutDefault[] = [];
  for (const entry of Array.isArray(defaults) ? defaults : []) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || entry.id === '') continue;
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push({
      id: entry.id,
      label: typeof entry.label === 'string' ? entry.label : entry.id,
      width: isLayoutWidth(entry.width) ? entry.width : 'full',
    });
  }
  return out;
}

function layoutsBranch(preferences: unknown): Record<string, unknown> | null {
  if (!isRecord(preferences)) return null;
  const layouts = preferences[LAYOUTS_KEY];
  return isRecord(layouts) ? layouts : null;
}

/** Whether the server holds ANY branch for this page — well-formed or not (Reset must delete either). */
export function hasSavedLayout(preferences: unknown, pageId: string): boolean {
  const layouts = layoutsBranch(preferences);
  if (layouts === null) return false;
  const branch = layouts[pageId];
  return branch !== null && branch !== undefined;
}

/**
 * The stored branch for one page, read at the border. `null` = absent OR
 * malformed (both mean the defaults apply). Entries without a string id are
 * dropped; a repeated id keeps its first entry.
 */
export function readSavedLayout(
  preferences: unknown,
  pageId: string,
): readonly SavedLayoutEntry[] | null {
  const layouts = layoutsBranch(preferences);
  if (layouts === null) return null;
  const branch = layouts[pageId];
  if (!isRecord(branch)) return null;
  if (branch.v !== LAYOUT_VERSION || !Array.isArray(branch.blocks)) return null;
  const seen = new Set<string>();
  const entries: SavedLayoutEntry[] = [];
  for (const raw of branch.blocks as unknown[]) {
    if (!isRecord(raw) || typeof raw.id !== 'string' || seen.has(raw.id)) continue;
    seen.add(raw.id);
    entries.push({
      id: raw.id,
      width: isLayoutWidth(raw.width) ? raw.width : null,
      hidden: raw.hidden === true,
    });
  }
  return entries;
}

/**
 * Saved order first (unknown ids dropped), then every declared id the saved list
 * never mentioned spliced in at its default index. `null` = the defaults.
 */
export function applySavedLayout(
  saved: readonly SavedLayoutEntry[] | null,
  defaults: readonly ResolvedLayoutDefault[],
): LayoutBlock[] {
  const defaultById = new Map<string, ResolvedLayoutDefault>();
  for (const d of defaults) defaultById.set(d.id, d);

  const savedById = new Map<string, SavedLayoutEntry>();
  const order: string[] = [];
  for (const entry of saved ?? []) {
    if (!defaultById.has(entry.id) || savedById.has(entry.id)) continue;
    savedById.set(entry.id, entry);
    order.push(entry.id);
  }
  defaults.forEach((d, index) => {
    if (savedById.has(d.id) || order.includes(d.id)) return;
    order.splice(Math.min(index, order.length), 0, d.id);
  });

  const blocks: LayoutBlock[] = [];
  for (const id of order) {
    const d = defaultById.get(id);
    if (d === undefined) continue;
    const s = savedById.get(id);
    blocks.push({
      id,
      label: d.label,
      width: s !== undefined && s.width !== null ? s.width : d.width,
      hidden: s?.hidden === true,
    });
  }
  return blocks;
}

export function toStoredLayout(blocks: readonly LayoutBlock[]): StoredLayoutBlock[] {
  return blocks.map(({ id, width, hidden }) => ({ id, width, hidden }));
}

/** Same order, same widths as declared, nothing hidden. */
export function isDefaultLayout(
  blocks: readonly LayoutBlock[],
  defaults: readonly ResolvedLayoutDefault[],
): boolean {
  if (blocks.length !== defaults.length) return false;
  return blocks.every((block, index) => {
    const d = defaults[index];
    return d !== undefined && d.id === block.id && d.width === block.width && !block.hidden;
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function moveIndex(list: readonly LayoutBlock[], from: number, to: number): LayoutBlock[] {
  const out = list.slice();
  const [item] = out.splice(from, 1);
  if (item === undefined) return out;
  out.splice(clamp(to, 0, out.length), 0, item);
  return out;
}

function sameOrder(a: readonly LayoutBlock[], b: readonly LayoutBlock[]): boolean {
  return a.length === b.length && a.every((block, index) => b[index]?.id === block.id);
}

/** Move a block to an index in the full list. `null` = nothing changes. */
export function moveBlockTo(
  list: readonly LayoutBlock[],
  id: string,
  toIndex: number,
): LayoutBlock[] | null {
  const from = list.findIndex((b) => b.id === id);
  if (from < 0 || !Number.isFinite(toIndex)) return null;
  const next = moveIndex(list, from, Math.trunc(toIndex));
  return sameOrder(next, list) ? null : next;
}

/**
 * Step a visible block past its next VISIBLE neighbour (hidden blocks are
 * stepped over, so Move up/down always visibly moves something). A hidden block
 * steps one slot in the full list. `null` = nothing changes (already at the end).
 */
export function moveBlockBy(
  list: readonly LayoutBlock[],
  id: string,
  delta: number,
): LayoutBlock[] | null {
  const step = Math.trunc(delta);
  const from = list.findIndex((b) => b.id === id);
  const self = list[from];
  if (self === undefined || step === 0 || !Number.isFinite(step)) return null;
  if (self.hidden) {
    const to = clamp(from + step, 0, list.length - 1);
    return to === from ? null : moveIndex(list, from, to);
  }
  const visible = list.filter((b) => !b.hidden);
  const vFrom = visible.findIndex((b) => b.id === id);
  const vTo = clamp(vFrom + step, 0, visible.length - 1);
  if (vTo === vFrom) return null;
  const target = visible[vTo];
  if (target === undefined) return null;
  return moveIndex(list, from, list.findIndex((b) => b.id === target.id));
}

export function setBlockWidth(
  list: readonly LayoutBlock[],
  id: string,
  width: LayoutWidth,
): LayoutBlock[] | null {
  if (!isLayoutWidth(width)) return null;
  const block = list.find((b) => b.id === id);
  if (block === undefined || block.width === width) return null;
  return list.map((b) => (b.id === id ? { ...b, width } : b));
}

export function toggleBlockHidden(list: readonly LayoutBlock[], id: string): LayoutBlock[] | null {
  if (!list.some((b) => b.id === id)) return null;
  return list.map((b) => (b.id === id ? { ...b, hidden: !b.hidden } : b));
}

/* ------------------------------------------------------------------------- */
/* The hook                                                                   */
/* ------------------------------------------------------------------------- */

type LoadStatus = 'loading' | 'ready' | 'failed';

interface Snapshot {
  pageId: string;
  status: LoadStatus;
  /** What is in force. `null` = nothing chosen → the defaults. */
  entries: readonly SavedLayoutEntry[] | null;
  /** The server holds a branch for this page (as of the last read or write). */
  stored: boolean;
  saveError: string | null;
}

/** An edit, kept as an operation so it can be replayed onto a layout that arrives later. */
type LayoutOp = (list: readonly LayoutBlock[]) => readonly LayoutBlock[] | null;

/** `blocks: null` deletes the page's branch. */
interface LayoutWrite {
  pageId: string;
  blocks: readonly StoredLayoutBlock[] | null;
}

function initialSnapshot(pageId: string): Snapshot {
  return { pageId, status: 'loading', entries: null, stored: false, saveError: null };
}

function writeBody(write: LayoutWrite): PreferencesUpdateInput {
  return {
    preferences: {
      [LAYOUTS_KEY]: {
        [write.pageId]:
          write.blocks === null
            ? null
            : { v: LAYOUT_VERSION, blocks: write.blocks.map((b) => ({ ...b })) },
      },
    },
  };
}

function saveErrorOf(error: unknown): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : SAVE_FAILED;
}

export function usePageLayout(pageId: string, defaults: readonly LayoutBlockDefault[]): PageLayout {
  // Keyed on content so an inline array does not churn every memo below.
  const signature = JSON.stringify(resolveLayoutDefaults(defaults));
  const defs = useMemo(() => JSON.parse(signature) as ResolvedLayoutDefault[], [signature]);
  const defsRef = useRef(defs);
  useEffect(() => {
    defsRef.current = defs;
  }, [defs]);

  const [snapshot, setSnapshot] = useState<Snapshot>(() => initialSnapshot(pageId));
  // The source of truth for callbacks (state is a render behind inside them).
  const snapRef = useRef<Snapshot>(snapshot);
  const held = useRef<LayoutOp[]>([]);
  const heldReset = useRef(false);

  const scheduled = useRef<LayoutWrite | null>(null); // waiting for the debounce
  // Ready to send, at most one per pageId (a newer write for a page replaces its older one).
  const queued = useRef<LayoutWrite[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const running = useRef(false);
  const generation = useRef(0);
  // Writes keep draining after unmount; state is only set while mounted.
  const mounted = useRef(true);

  const [editingState, setEditingState] = useState<{ pageId: string; on: boolean }>(() => ({
    pageId,
    on: false,
  }));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** The snapshot for THIS pageId, starting afresh when the id has changed. */
  const current = useCallback((): Snapshot => {
    if (snapRef.current.pageId !== pageId) {
      snapRef.current = initialSnapshot(pageId);
      held.current = [];
      heldReset.current = false;
    }
    return snapRef.current;
  }, [pageId]);

  const commit = useCallback((next: Snapshot) => {
    snapRef.current = next;
    if (mounted.current) setSnapshot(next);
  }, []);

  const nothingPending = useCallback(
    () => scheduled.current === null && queued.current.length === 0 && !running.current,
    [],
  );

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      for (;;) {
        const write = queued.current.shift();
        if (write === undefined) break;
        const sentAt = generation.current;
        try {
          const response = await api.updatePreferences(writeBody(write), undefined, true);
          const snap = snapRef.current;
          if (!mounted.current || snap.pageId !== write.pageId) continue;
          const preferences = response.data.preferences;
          // Render from what came back — unless a newer edit has overtaken it.
          const caughtUp =
            scheduled.current === null &&
            queued.current.length === 0 &&
            generation.current === sentAt;
          commit({
            ...snap,
            saveError: null,
            stored: hasSavedLayout(preferences, write.pageId),
            entries: caughtUp ? readSavedLayout(preferences, write.pageId) : snap.entries,
          });
        } catch (error) {
          // Keep the on-screen layout; a newer queued write, if any, is the retry.
          const snap = snapRef.current;
          if (mounted.current && snap.pageId === write.pageId) {
            commit({ ...snap, saveError: saveErrorOf(error) });
          }
        }
      }
    } finally {
      running.current = false;
    }
  }, [commit]);

  const flushNow = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const write = scheduled.current;
    if (write !== null) {
      scheduled.current = null;
      queued.current = [...queued.current.filter((w) => w.pageId !== write.pageId), write];
    }
    void drain();
  }, [drain]);

  const schedule = useCallback(
    (write: LayoutWrite) => {
      generation.current += 1;
      // A write for another page still pending goes out first, unmerged.
      if (scheduled.current !== null && scheduled.current.pageId !== write.pageId) flushNow();
      scheduled.current = write;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flushNow, SAVE_DEBOUNCE_MS);
    },
    [flushNow],
  );

  // The read: once per mount and per pageId. Its cleanup flushes a pending save.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    if (current().status === 'loading') {
      api
        .getPreferences(signal)
        .then((response) => {
          if (signal.aborted) return;
          const preferences = response.data.preferences;
          const now = current();
          const stored = hasSavedLayout(preferences, pageId);
          const wasReset = heldReset.current;
          const ops = held.current;
          held.current = [];
          heldReset.current = false;
          const base = wasReset ? null : readSavedLayout(preferences, pageId);

          const defsNow = defsRef.current;
          let list: readonly LayoutBlock[] = applySavedLayout(base, defsNow);
          let changed = false;
          for (const op of ops) {
            const next = op(list);
            if (next !== null) {
              list = next;
              changed = true;
            }
          }

          if (changed) {
            const entries = isDefaultLayout(list, defsNow) ? null : toStoredLayout(list);
            commit({ ...now, status: 'ready', stored, entries });
            if (entries !== null || stored) schedule({ pageId, blocks: entries });
          } else {
            commit({ ...now, status: 'ready', stored, entries: base });
            if (wasReset && stored) schedule({ pageId, blocks: null });
          }
        })
        .catch(() => {
          if (signal.aborted) return;
          /*
           * ⚠️ A FAILED READ DEGRADES TO THE DEFAULTS (plus held edits) AND
           * NEVER SAVES — see the header.
           */
          const now = current();
          const hadEdits = held.current.length > 0 || heldReset.current;
          held.current = [];
          heldReset.current = false;
          commit({ ...now, status: 'failed', saveError: hadEdits ? LOAD_FAILED : now.saveError });
        });
    }
    return () => {
      controller.abort();
      if (scheduled.current !== null) flushNow();
    };
  }, [pageId, current, commit, schedule, flushNow]);

  const edit = useCallback(
    (op: LayoutOp) => {
      const snap = current();
      const next = op(applySavedLayout(snap.entries, defs));
      if (next === null) return;
      const blocks = isDefaultLayout(next, defs) ? null : toStoredLayout(next);

      if (snap.status === 'loading') {
        held.current.push(op);
        commit({ ...snap, entries: blocks });
        return;
      }
      if (snap.status === 'failed') {
        commit({ ...snap, entries: blocks, saveError: LOAD_FAILED });
        return;
      }
      commit({ ...snap, entries: blocks });
      if (blocks === null && !snap.stored && nothingPending()) return;
      schedule({ pageId, blocks });
    },
    [current, commit, defs, nothingPending, schedule, pageId],
  );

  const move = useCallback(
    (id: string, toIndex: number) => edit((list) => moveBlockTo(list, id, toIndex)),
    [edit],
  );
  const moveBy = useCallback(
    (id: string, delta: -1 | 1) => edit((list) => moveBlockBy(list, id, delta)),
    [edit],
  );
  const setWidth = useCallback(
    (id: string, width: LayoutWidth) => edit((list) => setBlockWidth(list, id, width)),
    [edit],
  );
  const toggleHidden = useCallback(
    (id: string) => edit((list) => toggleBlockHidden(list, id)),
    [edit],
  );

  const reset = useCallback(() => {
    const snap = current();
    if (snap.status === 'loading') {
      held.current = [];
      heldReset.current = true;
      commit({ ...snap, entries: null });
      return;
    }
    if (snap.status === 'failed') {
      commit({ ...snap, entries: null });
      return;
    }
    // Nothing stored and nothing on its way: there is nothing to delete.
    if (snap.entries === null && !snap.stored && nothingPending()) return;
    commit({ ...snap, entries: null });
    schedule({ pageId, blocks: null });
  }, [current, commit, nothingPending, schedule, pageId]);

  const setEditing = useCallback(
    (next: boolean) => setEditingState({ pageId, on: next }),
    [pageId],
  );

  // Between a pageId change and its effect, render the new page's defaults.
  const view = useMemo(
    () => (snapshot.pageId === pageId ? snapshot : initialSnapshot(pageId)),
    [snapshot, pageId],
  );
  const editing = editingState.pageId === pageId && editingState.on;

  const blocks = useMemo(() => applySavedLayout(view.entries, defs), [view.entries, defs]);
  const byId = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks]);
  const isVisible = useCallback(
    (id: string) => {
      const block = byId.get(id);
      return block !== undefined && !block.hidden;
    },
    [byId],
  );
  const widthOf = useCallback((id: string): LayoutWidth => byId.get(id)?.width ?? 'full', [byId]);
  const isDefault = useMemo(() => isDefaultLayout(blocks, defs), [blocks, defs]);

  return useMemo(
    () => ({
      pageId,
      ready: view.status !== 'loading',
      editing,
      setEditing,
      blocks,
      isVisible,
      widthOf,
      move,
      moveBy,
      setWidth,
      toggleHidden,
      reset,
      isDefault,
      saveError: view.saveError,
    }),
    [
      pageId,
      view.status,
      view.saveError,
      editing,
      setEditing,
      blocks,
      isVisible,
      widthOf,
      move,
      moveBy,
      setWidth,
      toggleHidden,
      reset,
      isDefault,
    ],
  );
}
